/**
 * V70 Phase B — 참가신청 (/tournament/apply)
 *
 * 섹션
 *   A. 공개 양식 목록 — publicStatus='open' + 신청기간 내 (BE 가 기간 재검증)
 *   B. 내 신청 요약 — owner / member 참여 전부
 *   C. 양식 상세 + 신청 모달 — 동적 필드 (short/long/select/checkbox), scope 분기, 팀 UID 지정
 *
 * BE Callable
 *   - getPublicTournamentApplyForms
 *   - getPublicTournamentApplyForm
 *   - getMyTournamentApplications
 *   - createTournamentApplication
 *   - updateTournamentApplication
 *   - cancelTournamentApplication
 */
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useEmployees } from '../../shared/hooks/useEmployees';
import type { Employee } from '../../shared/types/employee';
// V70 Phase B 긴급 수정 (2026-10-20) — 관리자 화면과 상태 표시 **단일 소스** 공유 (지시 §10 일치 보장)
import {
  computeEffectiveFormStatus,
  effectiveStatusLabel,
  effectiveStatusColor,
  isAcceptingApplications,
  fmtKstDateTime,
} from './shared/applyFormStatus';
import { useTournamentQuery } from './shared/tournamentQueryCache';
// V70-J — 게임대회 공통 헤더
import { TournamentPageHeader, tournamentPageStyle } from './shared/TournamentPageHeader';
// V70-M — 게임대회 다크 테마 (V70-L 3차 승인 디자인 확대 적용)
import {
  DARK, DARK_ACCENT_TEXT, darkTintBadge, darkNeutralBadge, darkInput, darkWarnBox, darkInfoBox,
  darkCard, darkSectionTitle, darkPrimaryBtn, darkSecondaryBtn, darkErrorBox, darkEmptyBox,
  darkModalOverlay,
} from './shared/tournamentDarkTheme';

type FormMode = 'solo' | 'team';
type FormStatus = 'draft' | 'open' | 'paused' | 'closed' | 'archived';
type ApprovalMode = 'auto' | 'manual';
type FieldType = 'short' | 'long' | 'select' | 'checkbox';
type FieldScope = 'team' | 'member';
type MemberRole = 'owner' | 'member';
type ApplicationStatus = 'submitted' | 'approved' | 'rejected' | 'cancelled';
type FieldAnswer = string | string[];

interface Field {
  fieldId: string;
  label: string;
  type: FieldType;
  required: boolean;
  scope: FieldScope;
  options: string[] | null;
  order: number;
  deleted: boolean;
}

interface ApplyForm {
  formId: string;
  title: string;
  description: string;
  applyStartAt: { toMillis?: () => number; _seconds?: number };
  applyEndAt: { toMillis?: () => number; _seconds?: number };
  publicStatus: FormStatus;
  mode: FormMode;
  teamSize: number | null;
  approvalMode: ApprovalMode;
  fields: Field[];
  firstApplicationAt: unknown;
  schemaVersion: number;
  version: number;
}

interface PublicFormSummary {
  formId: string;
  title: string;
  description: string;
  mode: FormMode;
  teamSize: number | null;
  approvalMode: ApprovalMode;
  publicStatus: FormStatus;
  applyStartAt: { toMillis?: () => number; _seconds?: number };
  applyEndAt: { toMillis?: () => number; _seconds?: number };
  schemaVersion: number;
  fieldCount: number;
  coverImageUrl: string | null;
  myApplicationStatus: ApplicationStatus | null;
  myRole: MemberRole | null;
  /** V70G8J-2 — 현재 참가 신청 수 (submitted+approved). 구버전 서버·집계 실패 시 없음/null → 표시 안 함 */
  currentApplicantCount?: number | null;
}

/** V70G8J-2 — "현재 참가 신청 12명" / "현재 참가 신청 5팀". 값이 없으면 null (미표시) */
export function applicantCountLabel(mode: FormMode, count: number | null | undefined): string | null {
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) return null;
  return `현재 참가 신청 ${count.toLocaleString()}${mode === 'team' ? '팀' : '명'}`;
}

interface Application {
  applicationId: string;
  formId: string;
  ownerUid: string;
  ownerDisplayName: string;
  mode: FormMode;
  teamSize: number | null;
  memberUids: string[];
  teamAnswers: Record<string, FieldAnswer>;
  memberAnswers: Record<string, Record<string, FieldAnswer>>;
  status: ApplicationStatus;
  submittedAt: { toMillis?: () => number; _seconds?: number };
  rejectReason: string | null;
  version: number;
}

function tsToMs(ts: { toMillis?: () => number; _seconds?: number } | null | undefined): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts._seconds === 'number') return ts._seconds * 1000;
  return 0;
}

function fmtDate(ms: number): string {
  if (!ms) return '-';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function statusLabel(s: ApplicationStatus): string {
  return ({ submitted: '검토 대기', approved: '승인 완료', rejected: '반려', cancelled: '취소됨' } as const)[s];
}

function statusColor(s: ApplicationStatus): string {
  return ({ submitted: '#2563eb', approved: '#059669', rejected: '#dc2626', cancelled: '#6b7280' } as const)[s];
}

function newReqId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type MyApplicationListRow = { application: Application; formSummary: { title: string; mode: FormMode }; myRole: MemberRole };

export function TournamentApplyPage() {
  const { user, isLoading: userLoading } = useCurrentUser();
  const uid = user?.profile.id;

  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

  // V70-H — 양식 목록(참가 인원·내 상태 포함)과 내 신청 내역을 각각 병렬 조회해 도착한 섹션부터 표시.
  //   탭 재진입 시 이전 결과 즉시 표시 (30초 지나면 화면 유지한 채 뒤에서 재조회).
  //   신청·수정·취소 후에는 두 목록 모두 강제 재조회 → 참가 인원·내 신청 상태 즉시 갱신.
  const formsQuery = useTournamentQuery<PublicFormSummary[]>(uid ? `apply:forms:${uid}` : null, async () => {
    try {
      const fns = getFunctions();
      const r = await httpsCallable<Record<string, never>, { forms: PublicFormSummary[] }>(fns, 'getPublicTournamentApplyForms')({});
      return r.data?.forms || [];
    } catch (err) {
      console.warn('[TournamentApply] fetch failed:', err);
      throw err;
    }
  });
  const myAppsQuery = useTournamentQuery<MyApplicationListRow[]>(uid ? `apply:my:${uid}` : null, async () => {
    try {
      const fns = getFunctions();
      const r = await httpsCallable<Record<string, never>, { applications: MyApplicationListRow[] }>(fns, 'getMyTournamentApplications')({});
      return r.data?.applications || [];
    } catch (err) {
      console.warn('[TournamentApply] fetch failed:', err);
      throw err;
    }
  });
  const forms = formsQuery.data ?? [];
  const myApps = myAppsQuery.data ?? [];
  const formsError = !!formsQuery.error;
  const myAppsError = !!myAppsQuery.error;
  const hasError = formsError || myAppsError;
  const reload = () => { void formsQuery.reload(); void myAppsQuery.reload(); };

  if (userLoading) return <div style={{ padding: 24 }}>로딩 중…</div>;
  if (!uid) return <div style={{ padding: 24 }}>로그인이 필요합니다.</div>;

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* V70-J — e스포츠 공통 헤더. 기존 이용 안내(개인·팀 신청)는 헤더 아래 한 줄로 유지 */}
        <TournamentPageHeader page="apply" style={{ marginBottom: 8 }} />
        <p style={headerStyle}>
          공개된 신청양식을 선택하여 개인 또는 팀으로 참가 신청하세요.
        </p>

        {hasError && (
          <div style={errorBox}>
            <strong>신청 양식을 불러오지 못했습니다.</strong>
            <div style={{ marginTop: 4, fontSize: 12 }}>
              네트워크 또는 서버 오류로 양식을 조회하지 못했습니다. 잠시 후 새로고침 해 주세요.
              (문제가 지속되면 관리자에게 문의)
            </div>
          </div>
        )}

        <style>{APPLY_CARD_LAYOUT_CSS}</style>
        <section style={{ marginBottom: 28 }}>
          <h2 style={sectionTitle}>공개된 신청 양식</h2>
          {formsQuery.loading && <div style={placeholderStyle}>로딩 중…</div>}
          {!formsQuery.loading && !formsError && forms.length === 0 && (
            <div style={placeholderStyle}>
              현재 공개된 신청 양식이 없습니다. 관리자가 양식을 공개하면 이곳에 노출됩니다.
            </div>
          )}
          {!formsQuery.loading && forms.length > 0 && (
            <div style={{ display: 'grid', gap: 12 }}>
              {forms.map((f) => (
                <FormCard
                  key={f.formId}
                  form={f}
                  onOpen={() => setSelectedFormId(f.formId)}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 style={sectionTitle}>내 신청 내역</h2>
          {myAppsQuery.loading && <div style={placeholderStyle}>로딩 중…</div>}
          {!myAppsQuery.loading && !myAppsError && myApps.length === 0 && (
            <div style={placeholderStyle}>아직 신청한 내역이 없습니다.</div>
          )}
          {!myAppsQuery.loading && myApps.length > 0 && (
            <div style={{ display: 'grid', gap: 10 }}>
              {myApps.map((row) => (
                <MyApplicationRow
                  key={row.application.applicationId + ':' + row.myRole}
                  application={row.application}
                  formTitle={row.formSummary.title}
                  mode={row.formSummary.mode}
                  myRole={row.myRole}
                  onOpenForm={() => setSelectedFormId(row.application.formId)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {selectedFormId && (
        <ApplyFormModal
          formId={selectedFormId}
          ownerUid={uid}
          onClose={() => setSelectedFormId(null)}
          onSubmitted={() => { setSelectedFormId(null); reload(); }}
        />
      )}
    </div>
  );
}

function FormCard({ form, onOpen }: { form: PublicFormSummary; onOpen: () => void }) {
  // V70 Phase B 긴급 수정 (2026-10-20) — 관리자 화면과 동일 매핑 적용 (지시 §10)
  const effective = computeEffectiveFormStatus(form.publicStatus, form.applyStartAt, form.applyEndAt);
  const accepting = isAcceptingApplications(form.publicStatus, form.applyStartAt, form.applyEndAt);
  const startMs = tsToMs(form.applyStartAt);
  const endMs = tsToMs(form.applyEndAt);
  const countLabel = applicantCountLabel(form.mode, form.currentApplicantCount);

  return (
    // V70-E — PC 3영역 레이아웃: [썸네일] [대회 정보] [신청 상태·인원·버튼(고정 폭, 상단 정렬)]
    //   가운데 설명이 길어져도 오른쪽 영역은 카드 상단 기준으로 고정. 모바일(≤720px)은 세로 재배치.
    <div style={cardStyle} className="v70e-apply-card" data-testid="apply-form-card">
      <div className="v70e-apply-card__grid">
        <div style={formCardImageWrap} data-testid="apply-card-image">
          {form.coverImageUrl ? (
            <img src={form.coverImageUrl} alt={form.title} style={formCardImage} loading="lazy" />
          ) : (
            <div style={formCardImagePlaceholder}>
              <span role="img" aria-label="placeholder" style={{ fontSize: 32, opacity: 0.4 }}>🏆</span>
            </div>
          )}
        </div>
        <div className="v70e-apply-card__info" data-testid="apply-card-info">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, overflowWrap: 'anywhere', color: DARK.text }}>{form.title}</h3>
          <p style={{ margin: 0, fontSize: 12, color: DARK.textMuted, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {form.description || '설명 없음'}
          </p>
          <div style={{ fontSize: 11, color: DARK.textMuted }}>
            신청기간: {fmtKstDateTime(startMs)} ~ {fmtKstDateTime(endMs)} · 항목 수: {form.fieldCount}
          </div>
        </div>
        <div className="v70e-apply-card__action" data-testid="apply-card-action">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ ...badgeStyle, ...darkTintBadge(effectiveStatusColor(effective)) }}>
              {effectiveStatusLabel(effective)}
            </span>
            <span style={badgeStyle}>{form.mode === 'solo' ? '개인' : `팀 ${form.teamSize}명`}</span>
            <span style={badgeStyle}>{form.approvalMode === 'auto' ? '자동 승인' : '수동 승인'}</span>
            {form.myApplicationStatus && (
              <span style={{ ...badgeStyle, ...darkTintBadge(statusColor(form.myApplicationStatus)) }}>
                내 상태: {statusLabel(form.myApplicationStatus)}
              </span>
            )}
          </div>
          {countLabel && (
            <div style={{ fontSize: 13, fontWeight: 700, color: DARK_ACCENT_TEXT }} data-testid="apply-current-count">
              {countLabel}
            </div>
          )}
          <button style={{ ...primaryButton, width: '100%' }} onClick={onOpen}>
            {form.myApplicationStatus ? '내 신청 보기' : accepting ? '신청하기' : '양식 보기'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * V70-E — 신청 카드 3영역 반응형 레이아웃 (inline style 로는 미디어쿼리 불가 → 범위 한정 class)
 * V70-M — 태블릿 가로 넘침 수정: AppShell 사이드바(280px)·여백 때문에 실제 본문 폭 = 화면 폭 − 344px.
 *   3열 최소 폭(180 + 220 + 간격 + 정보 영역)이 들어가지 않는 화면 950px 이하는 1열로 (PC 3열 구조는 그대로).
 */
export const APPLY_CARD_LAYOUT_CSS = `
.v70e-apply-card__grid {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) 220px;
  gap: 14px;
  align-items: start;
}
.v70e-apply-card__info { min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.v70e-apply-card__action { min-width: 0; display: flex; flex-direction: column; gap: 8px; align-self: start; }
@media (max-width: 950px) {
  .v70e-apply-card__grid { grid-template-columns: minmax(0, 1fr); }
}
`;

const formCardImageWrap: React.CSSProperties = {
  width: 180,
  minWidth: 160,
  height: 120,
  flexShrink: 0,
  background: DARK.surface,
  borderRadius: 8,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const formCardImage: React.CSSProperties = {
  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
};
const formCardImagePlaceholder: React.CSSProperties = {
  width: '100%', height: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: `linear-gradient(135deg, ${DARK.surface} 0%, ${DARK.card} 100%)`,
};

function MyApplicationRow({
  application, formTitle, mode, myRole, onOpenForm,
}: {
  application: Application; formTitle: string; mode: FormMode; myRole: MemberRole; onOpenForm: () => void;
}) {
  return (
    <div style={{ ...cardStyle, padding: '12px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DARK.text }}>{formTitle}</h4>
            <span style={badgeStyle}>{mode === 'solo' ? '개인' : '팀'}</span>
            <span style={badgeStyle}>{myRole === 'owner' ? '팀장(본인)' : '팀원'}</span>
            <span style={{ ...badgeStyle, ...darkTintBadge(statusColor(application.status)) }}>
              {statusLabel(application.status)}
            </span>
          </div>
          <div style={{ fontSize: 11, color: DARK.textMuted, marginTop: 4 }}>
            {application.ownerDisplayName && myRole === 'member' && `팀장: ${application.ownerDisplayName}`}
            {' · '}제출: {fmtKstDateTime(tsToMs(application.submittedAt))}
          </div>
          {application.status === 'rejected' && application.rejectReason && (
            <div style={{ marginTop: 4, color: '#FCA5A5', fontSize: 12 }}>반려 사유: {application.rejectReason}</div>
          )}
        </div>
        <button style={secondaryButton} onClick={onOpenForm}>양식 열기</button>
      </div>
    </div>
  );
}

function ApplyFormModal({
  formId, ownerUid, onClose, onSubmitted,
}: {
  formId: string; ownerUid: string; onClose: () => void; onSubmitted: () => void;
}) {
  const { user } = useCurrentUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [form, setForm] = useState<ApplyForm | null>(null);
  const [myApplication, setMyApplication] = useState<Application | null>(null);
  const [memberUids, setMemberUids] = useState<string[]>([]);
  const [teamAnswers, setTeamAnswers] = useState<Record<string, FieldAnswer>>({});
  const [memberAnswers, setMemberAnswers] = useState<Record<string, Record<string, FieldAnswer>>>({});
  const [memberLookupInput, setMemberLookupInput] = useState<string>('');
  const [memberUidByUid, setMemberUidByUid] = useState<Record<string, { name: string; employeeNumber: string }>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 직원 검색 — forTransfer 모드 (인증 사용자 전원 호출 가능, 같은 companyCode 활성 직원 반환)
  const { employees: searchableEmployees } = useEmployees(form?.mode === 'team' ? { forTransfer: true } : {});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setHasError(false);
      try {
        const fns = getFunctions();
        const r = await httpsCallable<{ formId: string }, {
          form: ApplyForm; myApplication: Application | null; myMemberClaims: unknown[];
        }>(fns, 'getPublicTournamentApplyForm')({ formId });
        if (cancelled) return;
        setForm(r.data.form);
        setMyApplication(r.data.myApplication || null);
        // V70 QA 수정 #1 (2026-10-20) — cancelled 신청은 폼 복원 X (신규 작성용 빈 폼).
        // V70 QA 수정 #7 (2026-10-21) — 반려 신청은 과거 제출 내용을 복원하여 사용자가 수정 후 재신청 가능.
        //   활성 신청(submitted/approved) : 수정 모드로 복원.
        //   반려 신청(rejected)           : 참고용 복원 → 재신청 (createTournamentApplication 가 overwrite).
        //   취소 신청(cancelled)          : 복원 안 함 (신규 작성 빈 폼).
        const app = r.data.myApplication;
        const restoreApp = !!app && (app.status === 'submitted' || app.status === 'approved' || app.status === 'rejected');
        if (restoreApp && app) {
          setMemberUids(app.memberUids || []);
          setTeamAnswers(app.teamAnswers || {});
          setMemberAnswers(app.memberAnswers || {});
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('[ApplyFormModal] fetch failed:', err);
        setHasError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formId]);

  // V70 Phase B UX 개선 (2026-10-20) — 기존 신청 재오픈 시에도 memberUids → 이름 백필
  //   (사용자 지시 §2 "UID 를 사용자에게 직접 노출하지 말 것")
  useEffect(() => {
    if (!searchableEmployees || searchableEmployees.length === 0) return;
    if (memberUids.length === 0) return;
    setMemberUidByUid((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const u of memberUids) {
        if (next[u]) continue;
        const emp = searchableEmployees.find((e) => e.id === u);
        if (emp) {
          next[u] = { name: emp.nameKo || '-', employeeNumber: emp.employeeNumber || '' };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [searchableEmployees, memberUids]);

  // V70 QA 추가수정 (2026-10-20) — 팀원이 조회할 때 팀장 UID 를 이름으로 매핑.
  //   myApplication.ownerDisplayName (BE snapshot) 을 memberUidByUid 에 백필.
  //   본인 UID 는 ownerDisplay 가 담당하므로 제외.
  useEffect(() => {
    if (!myApplication) return;
    const uid = myApplication.ownerUid;
    if (uid === ownerUid) return;
    const name = (myApplication.ownerDisplayName || '').trim();
    if (!name) return;
    // searchableEmployees 매칭도 시도 (더 정확한 사번/소속)
    const emp = (searchableEmployees || []).find((e) => e.id === uid);
    setMemberUidByUid((prev) => {
      if (prev[uid]) return prev;
      return {
        ...prev,
        [uid]: {
          name: emp?.nameKo || name,
          employeeNumber: emp?.employeeNumber || '',
        },
      };
    });
  }, [myApplication, ownerUid, searchableEmployees]);

  // V70 QA 수정 #2 (2026-10-20) — 본인 이름 fallback 보강
  //   1) user.profile.displayName (useCurrentUser 가 Firestore users/{uid} 구독)
  //   2) users/{uid} 안의 nameKo (profile.displayName 비어있는 경우 — 일부 테스트 계정)
  //   3) searchableEmployees (forTransfer) 에서 매칭
  //   → 전부 실패 시 "(이름 조회 실패)" 로 표시 (UID 노출 금지)
  const [selfProfileFallback, setSelfProfileFallback] = useState<{ nameKo?: string; orgUnitName?: string } | null>(null);
  useEffect(() => {
    if (!ownerUid) return;
    if (user?.profile.displayName && user.profile.displayName.trim()) return;
    // profile 에 displayName 없음 — users/{uid} 직접 조회 (Rules 로 본인 read 허용)
    import('../../firebase/config').then(async ({ db: fdb }) => {
      if (!fdb) return;
      const { doc: fdoc, getDoc } = await import('firebase/firestore');
      try {
        const s = await getDoc(fdoc(fdb, 'users', ownerUid));
        if (s.exists()) {
          const d = s.data() as { nameKo?: string; orgUnitName?: string };
          setSelfProfileFallback({ nameKo: d.nameKo, orgUnitName: d.orgUnitName });
        }
      } catch { /* noop */ }
    });
  }, [ownerUid, user]);

  const ownerDisplay = useMemo(() => {
    const own = (searchableEmployees || []).find((e) => e.id === ownerUid);
    const name = (user?.profile.displayName && user.profile.displayName.trim())
      || (selfProfileFallback?.nameKo && selfProfileFallback.nameKo.trim())
      || (own?.nameKo && own.nameKo.trim())
      || '(이름 조회 실패)';
    const org = (selfProfileFallback?.orgUnitName || own?.orgUnitName) || '';
    return org ? `${name} · ${org}` : name;
  }, [searchableEmployees, user, ownerUid, selfProfileFallback]);

  function labelForMember(u: string): string {
    if (u === ownerUid) return ownerDisplay;
    const resolved = memberUidByUid[u];
    if (!resolved) return '(이름 조회 중…)';
    return `${resolved.name}${resolved.employeeNumber ? ` (${resolved.employeeNumber})` : ''}`;
  }

  const activeFields: Field[] = useMemo(() => (form?.fields || []).filter((f) => !f.deleted).sort((a, b) => a.order - b.order), [form]);
  const teamFields = activeFields.filter((f) => f.scope === 'team');
  const memberFields = activeFields.filter((f) => f.scope === 'member');
  const requiredTeamSize = form?.mode === 'team' ? (form.teamSize ?? 0) : 1;
  const nextTeamSize = (form?.mode === 'team' ? memberUids.length + 1 : 1);
  const canSubmit = !!form && (form.mode === 'solo' || nextTeamSize === requiredTeamSize);
  const applyEndMs = tsToMs(form?.applyEndAt);
  // V70 Phase B 긴급 수정 (2026-10-20) — 신청 가능 여부는 공용 유틸(effective in-progress) 로 통일
  const isClosed = form ? !isAcceptingApplications(form.publicStatus, form.applyStartAt, form.applyEndAt) : false;
  // V70 QA 수정 #1 (2026-10-20) — cancelled 신청은 "활성 신청" 으로 간주하지 않음.
  //   취소 후 재신청 가능. rejected 는 재신청 금지.
  const hasActiveApplication = myApplication
    && myApplication.ownerUid === ownerUid
    && (myApplication.status === 'submitted' || myApplication.status === 'approved');
  const isOwnerExisting = hasActiveApplication;
  const canModifyExisting = !!hasActiveApplication;

  // V70 QA 추가수정 (2026-10-20) — 팀원이 신청서를 조회할 때 "팀장" 자리에 자기 자신이 아닌 실제 ownerUid 를 노출.
  //   isReadOnlyParticipant: 팀원 참여자가 조회 모드로 열어본 경우 (수정 권한 없음, 데이터 변경 금지)
  //   effectiveOwnerUid: 실제 신청의 팀장 UID (myApplication 있으면 그 ownerUid, 없으면 로그인 사용자)
  //   effectiveMemberUids: 조회 모드 — 신청의 멤버, 신규 작성 — FE state
  //   isSelfOwner: 로그인 사용자가 실제 팀장?
  const isReadOnlyParticipant = !!myApplication
    && myApplication.ownerUid !== ownerUid;
  const effectiveOwnerUid = myApplication?.ownerUid ?? ownerUid;
  const effectiveMemberUids = isReadOnlyParticipant
    ? (myApplication!.memberUids || [])
    : memberUids;
  const isSelfOwner = effectiveOwnerUid === ownerUid;

  function setAnswerForField(scope: FieldScope, uid: string, fieldId: string, value: FieldAnswer): void {
    if (scope === 'team') {
      setTeamAnswers((prev) => ({ ...prev, [fieldId]: value }));
    } else {
      setMemberAnswers((prev) => ({
        ...prev,
        [uid]: { ...(prev[uid] || {}), [fieldId]: value },
      }));
    }
  }

  function addMemberByEmployee(emp: Employee): void {
    const u = emp.id;
    if (u === ownerUid) { setSubmitError('본인을 팀원으로 지정할 수 없습니다.'); return; }
    if (memberUids.includes(u)) { setSubmitError('이미 추가된 팀원입니다.'); return; }
    if (memberUids.length + 2 > requiredTeamSize) { setSubmitError(`팀 인원 ${requiredTeamSize}명 초과`); return; }
    setMemberUids((prev) => [...prev, u]);
    setMemberUidByUid((prev) => ({
      ...prev,
      [u]: { name: emp.nameKo || '-', employeeNumber: emp.employeeNumber || '' },
    }));
    setMemberLookupInput('');
    setSubmitError(null);
  }

  function removeMember(u: string): void {
    setMemberUids((prev) => prev.filter((x) => x !== u));
    setMemberAnswers((prev) => {
      const copy = { ...prev }; delete copy[u]; return copy;
    });
    setMemberUidByUid((prev) => {
      const copy = { ...prev }; delete copy[u]; return copy;
    });
  }

  // 자동완성 결과 — 이름/사번/이메일 contains 매칭. 자신/이미 추가된 UID 제외.
  const employeeSearchResults = useMemo<Employee[]>(() => {
    const q = memberLookupInput.trim().toLowerCase();
    if (q.length < 1) return [];
    const addedSet = new Set<string>([ownerUid, ...memberUids]);
    const matches: Employee[] = [];
    for (const e of (searchableEmployees || [])) {
      if (addedSet.has(e.id)) continue;
      const hay = `${e.nameKo || ''} ${e.employeeNumber || ''} ${e.email || ''}`.toLowerCase();
      if (hay.includes(q)) {
        matches.push(e);
        if (matches.length >= 10) break;
      }
    }
    return matches;
  }, [memberLookupInput, searchableEmployees, memberUids, ownerUid]);

  async function handleSubmit(): Promise<void> {
    if (!form) return;
    setSubmitError(null); setSubmitting(true);
    try {
      const fns = getFunctions();
      const payload = {
        formId: form.formId,
        memberUids: form.mode === 'team' ? memberUids : [],
        teamAnswers,
        memberAnswers,
        clientRequestId: newReqId(),
      };
      if (canModifyExisting) {
        await httpsCallable(fns, 'updateTournamentApplication')({ ...payload, expectedVersion: myApplication!.version });
      } else {
        await httpsCallable(fns, 'createTournamentApplication')(payload);
      }
      onSubmitted();
    } catch (err) {
      setSubmitError(formatSubmitError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // V70 QA 수정 #4·#5 (2026-10-20) — BE HttpsError.details 기반 이름 변환
  //   details.kind:
  //     'missing-required-field' → "필수 항목을 입력해주세요 (이름): 라벨"
  //     'duplicate-member'       → "이미 다른 신청에 참여 중인 팀원: 이름"
  //   UID 는 노출 금지. 이름 조회 실패 시 "팀원" 또는 "지정된 참가자" 로 안전한 대체.
  function formatSubmitError(err: unknown): string {
    const e = err as { code?: string; message?: string; details?: any };
    const details = e?.details;
    if (details?.kind === 'missing-required-field') {
      const uid: string | null = details.uid ?? null;
      const label: string = details.fieldLabel || '필수 항목';
      if (uid) {
        const name = resolveUidName(uid) || '팀원';
        return `필수 항목을 입력해주세요 (${name}): ${label}`;
      }
      return `필수 항목을 입력해주세요: ${label}`;
    }
    if (details?.kind === 'duplicate-member') {
      const uid: string | null = details.conflictUid ?? null;
      const name = uid ? (resolveUidName(uid) || '지정된 참가자') : '지정된 참가자';
      return `이미 다른 신청에 참여 중인 팀원: ${name}`;
    }
    return e?.message || e?.code || '신청 처리 실패';
  }

  function resolveUidName(uid: string): string | null {
    if (uid === ownerUid) {
      const own = (searchableEmployees || []).find((em) => em.id === ownerUid);
      return (user?.profile.displayName && user.profile.displayName.trim())
        || (selfProfileFallback?.nameKo && selfProfileFallback.nameKo.trim())
        || (own?.nameKo && own.nameKo.trim())
        || null;
    }
    const resolved = memberUidByUid[uid];
    if (resolved) return resolved.name + (resolved.employeeNumber ? ` (${resolved.employeeNumber})` : '');
    const emp = (searchableEmployees || []).find((em) => em.id === uid);
    if (emp) return emp.nameKo + (emp.employeeNumber ? ` (${emp.employeeNumber})` : '');
    return null;
  }

  async function handleCancel(): Promise<void> {
    if (!form || !myApplication) return;
    // V70 QA 수정 (2026-10-20) — 지시: 개인전·팀전 공용 간결 문구. 실제 claim 해제 로직은 BE 그대로.
    if (!window.confirm('신청을 취소하시겠습니까?')) return;
    setSubmitError(null); setSubmitting(true);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'cancelTournamentApplication')({ formId: form.formId, clientRequestId: newReqId() });
      onSubmitted();
    } catch (err) {
      const msg = (err as { message?: string })?.message;
      setSubmitError(msg || '취소 실패');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // V70 QA 수정 #3 (2026-10-20): overlay onClick 제거 — 외부 클릭·드래그·ESC 로 닫힘 금지. [닫기] 버튼 또는 신청 완료 시만.
    <div style={overlayStyle}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {loading && <div style={{ padding: 32, textAlign: 'center', color: DARK.textMuted }}>로딩 중…</div>}
        {!loading && hasError && (
          <div style={{ padding: 24 }}>
            <div style={errorBox}>양식을 불러오지 못했습니다.</div>
            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <button style={secondaryButton} onClick={onClose}>닫기</button>
            </div>
          </div>
        )}
        {!loading && form && (
          <>
            <div style={{ padding: '18px 20px 12px', borderBottom: `1px solid ${DARK.border}` }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: DARK.text }}>{form.title}</h2>
              <div style={{ fontSize: 12, color: DARK.textMuted, marginTop: 4 }}>
                신청기간: {fmtKstDateTime(tsToMs(form.applyStartAt))} ~ {fmtKstDateTime(applyEndMs)}
                {' · '}모드: {form.mode === 'solo' ? '개인' : `팀 ${form.teamSize}명`}
                {' · '}승인: {form.approvalMode === 'auto' ? '자동' : '수동'}
              </div>
              {form.description && <p style={{ margin: '8px 0 0', fontSize: 12, color: DARK.textMuted, whiteSpace: 'pre-wrap' }}>{form.description}</p>}
            </div>

            <div style={{ padding: 20, maxHeight: '60vh', overflowY: 'auto' }}>
              {isClosed && (
                <div style={{ ...errorBox, ...darkWarnBox }}>
                  이 양식은 현재 신청을 받고 있지 않습니다.
                </div>
              )}

              {myApplication && (
                <div style={{ ...darkInfoBox, marginBottom: 16, padding: 12, borderRadius: 8, fontSize: 12 }}>
                  <strong>현재 내 상태:</strong>{' '}
                  <span style={{ color: darkTintBadge(statusColor(myApplication.status)).color, fontWeight: 700 }}>
                    {statusLabel(myApplication.status)}
                  </span>
                  {myApplication.ownerUid !== ownerUid && (
                    <span> (팀원 참여 — 신청 수정/취소는 팀장만 가능)</span>
                  )}
                  {myApplication.status === 'rejected' && myApplication.rejectReason && (
                    <div style={{ color: '#FCA5A5', marginTop: 4 }}>반려 사유: {myApplication.rejectReason}</div>
                  )}
                </div>
              )}

              {form.mode === 'team' && (
                <section style={{ marginBottom: 16 }}>
                  <h3 style={subTitleStyle}>팀 구성 (팀장 포함 {requiredTeamSize}명 필요, 현재 {nextTeamSize}명)</h3>
                  {/* V70 QA 추가 보완 (2026-10-20) — 팀원 조회 모드(isReadOnlyParticipant) 에서는 팀원 추가 input/자동완성 전체 숨김. */}
                  {!isReadOnlyParticipant && (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder={nextTeamSize >= requiredTeamSize ? '팀원 정원이 충족되었습니다' : '팀원 이름 또는 사번으로 검색'}
                      value={memberLookupInput}
                      onChange={(e) => setMemberLookupInput(e.target.value)}
                      style={inputStyle}
                      /* V70 QA 수정 #2 (2026-10-21) — 팀원 정원 충족 시 비활성화. 팀원 삭제 시 자동 재활성화. */
                      disabled={isClosed || submitting || (!!isOwnerExisting && !canModifyExisting) || nextTeamSize >= requiredTeamSize}
                    />
                    {employeeSearchResults.length > 0 && !isClosed && !submitting && (
                      <div style={suggestBox}>
                        {employeeSearchResults.map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            style={suggestItem}
                            onClick={() => addMemberByEmployee(e)}
                          >
                            <div style={{ fontWeight: 600, fontSize: 13, color: DARK.text }}>{e.nameKo || '(이름 없음)'}</div>
                            <div style={{ fontSize: 11, color: DARK.textMuted }}>
                              {e.employeeNumber || '-'}
                              {e.orgUnitName ? ` · ${e.orgUnitName}` : ''}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {memberLookupInput.trim().length > 0 && employeeSearchResults.length === 0 && (
                      <div style={{ ...suggestBox, padding: 10, color: DARK.textMuted, fontSize: 12 }}>
                        일치하는 활성 직원이 없습니다.
                      </div>
                    )}
                  </div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {/* V70 QA 추가수정 — 팀장은 effectiveOwnerUid 기준. 팀원이 조회 시 실제 팀장 이름 표시. */}
                    <span style={memberPillOwner}>
                      {isSelfOwner ? `팀장(본인): ${ownerDisplay}` : `팀장: ${labelForMember(effectiveOwnerUid)}`}
                    </span>
                    {effectiveMemberUids.map((u) => (
                      <span key={u} style={memberPill}>
                        {u === ownerUid && !isSelfOwner ? `${labelForMember(u)} · 본인` : labelForMember(u)}
                        {!isReadOnlyParticipant && (
                          <button style={pillDelBtn} onClick={() => removeMember(u)} disabled={isClosed || submitting}>×</button>
                        )}
                      </span>
                    ))}
                  </div>
                  {/* V70 QA 수정 #5 (2026-10-21) — 팀원 부족 시 명확한 안내. [신청하기] 버튼은 disabled. */}
                  {!isReadOnlyParticipant && nextTeamSize < requiredTeamSize && (
                    <div style={{ ...darkWarnBox, marginTop: 8, padding: 8, borderRadius: 6, fontSize: 12 }}>
                      팀 구성 인원이 부족합니다. 팀장 포함 {requiredTeamSize}명이 되어야 신청할 수 있습니다. (현재 {nextTeamSize}명)
                    </div>
                  )}
                </section>
              )}

              {teamFields.length > 0 && (
                <section style={{ marginBottom: 14 }}>
                  <h3 style={subTitleStyle}>팀 공통 항목</h3>
                  {teamFields.map((field) => (
                    <FieldInput
                      key={field.fieldId}
                      field={field}
                      value={(isReadOnlyParticipant ? (myApplication?.teamAnswers?.[field.fieldId]) : teamAnswers[field.fieldId]) ?? (field.type === 'checkbox' ? [] : '')}
                      onChange={(v) => setAnswerForField('team', effectiveOwnerUid, field.fieldId, v)}
                      disabled={isClosed || submitting || isReadOnlyParticipant}
                    />
                  ))}
                </section>
              )}

              {memberFields.length > 0 && (
                <section style={{ marginBottom: 14 }}>
                  <h3 style={subTitleStyle}>구성원 개별 항목</h3>
                  {/* V70 QA 추가수정 — effectiveOwnerUid 로 팀장을 결정. 팀원이 조회 시에도 실제 팀장이 최상단. */}
                  {[effectiveOwnerUid, ...(form.mode === 'team' ? effectiveMemberUids : [])].map((u) => (
                    <div key={u} style={{ marginTop: 10, padding: 10, border: `1px solid ${DARK.border}`, borderRadius: 8, background: DARK.row }}>
                      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: DARK.text }}>
                        {u === effectiveOwnerUid
                          ? (isSelfOwner ? `팀장(본인) · ${ownerDisplay}` : `팀장 · ${labelForMember(u)}`)
                          : (u === ownerUid ? `팀원(본인) · ${ownerDisplay}` : `팀원 · ${labelForMember(u)}`)}
                      </div>
                      {memberFields.map((field) => (
                        <FieldInput
                          key={field.fieldId}
                          field={field}
                          value={(isReadOnlyParticipant ? (myApplication?.memberAnswers?.[u]?.[field.fieldId]) : memberAnswers[u]?.[field.fieldId]) ?? (field.type === 'checkbox' ? [] : '')}
                          onChange={(v) => setAnswerForField('member', u, field.fieldId, v)}
                          disabled={isClosed || submitting || isReadOnlyParticipant}
                        />
                      ))}
                    </div>
                  ))}
                </section>
              )}

              {submitError && <div style={{ ...errorBox, marginTop: 10 }}>{submitError}</div>}
            </div>

            <div style={{ padding: '12px 20px', borderTop: `1px solid ${DARK.border}`, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                {canModifyExisting && (
                  <button style={dangerButton} onClick={handleCancel} disabled={submitting || isClosed}>
                    신청 취소
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={secondaryButton} onClick={onClose} disabled={submitting}>닫기</button>
                {/* V70 QA 수정 #4 (2026-10-21) — 팀원 조회 모드(isReadOnlyParticipant)에서는 [신청하기]/[수정 저장] 모두 미노출.
                      신청 권한은 팀장(ownerUid)만. 팀원은 읽기 전용으로 자신의 답변만 확인 가능. */}
                {!isReadOnlyParticipant && (!isOwnerExisting || canModifyExisting) && (
                  <button
                    style={primaryButton}
                    onClick={handleSubmit}
                    disabled={submitting || isClosed || !canSubmit}
                  >
                    {submitting ? '처리 중…' : canModifyExisting ? '수정 저장' : '신청하기'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FieldInput({
  field, value, onChange, disabled,
}: {
  field: Field;
  value: FieldAnswer;
  onChange: (v: FieldAnswer) => void;
  disabled: boolean;
}) {
  const label = (
    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: DARK.text }}>
      {(field.label && field.label.trim()) || '(라벨 없음)'} {field.required && <span style={{ color: '#F87171' }}>*</span>}
    </div>
  );
  if (field.type === 'short') {
    return (
      <div style={{ marginTop: 8 }}>
        {label}
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
          disabled={disabled}
          maxLength={200}
        />
      </div>
    );
  }
  if (field.type === 'long') {
    return (
      <div style={{ marginTop: 8 }}>
        {label}
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          disabled={disabled}
          maxLength={2000}
        />
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div style={{ marginTop: 8 }}>
        {label}
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
          disabled={disabled}
        >
          <option value="">선택하세요</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === 'checkbox') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div style={{ marginTop: 8 }}>
        {label}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {(field.options || []).map((opt) => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: DARK.text, colorScheme: 'dark' }}>
              <input
                type="checkbox"
                checked={arr.includes(opt)}
                onChange={(e) => {
                  if (e.target.checked) onChange([...arr, opt]);
                  else onChange(arr.filter((x) => x !== opt));
                }}
                disabled={disabled}
              />
              {opt}
            </label>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// ─── 스타일 ───
// V70-M — 게임대회 다크 테마 (페이지 #22262E · 카드 #111318 · 행 #0B0D12 · 보조 #191C24 · 테두리 #303540)
const pageStyle: React.CSSProperties = tournamentPageStyle({ x: 16, y: 20 });
const headerStyle: React.CSSProperties = { margin: '0 0 20px', color: DARK.textMuted, fontSize: 13 };
const sectionTitle: React.CSSProperties = { ...darkSectionTitle, fontSize: 15, margin: '12px 0 10px' };
const subTitleStyle: React.CSSProperties = { fontSize: 13, fontWeight: 700, margin: '12px 0 6px', color: DARK.text };
const cardStyle: React.CSSProperties = { ...darkCard, padding: 14, color: DARK.text };
const badgeStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
  ...darkNeutralBadge,
};
const primaryButton: React.CSSProperties = { ...darkPrimaryBtn, padding: '8px 14px', fontSize: 13 };
const secondaryButton: React.CSSProperties = { ...darkSecondaryBtn, padding: '8px 14px', fontSize: 13 };
// 신청 취소 — 위험 의미 빨강 유지 (흰 글자 대비 4.8:1)
const dangerButton: React.CSSProperties = {
  padding: '8px 14px', background: '#dc2626', color: '#fff', border: '1px solid #dc2626',
  borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};
const placeholderStyle: React.CSSProperties = darkEmptyBox;
const errorBox: React.CSSProperties = { ...darkErrorBox };
const overlayStyle: React.CSSProperties = darkModalOverlay;
const modalStyle: React.CSSProperties = {
  background: DARK.card, color: DARK.text, border: `1px solid ${DARK.border}`, borderRadius: 12,
  width: '100%', maxWidth: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
  colorScheme: 'dark',
};
// V70 QA 리오픈 NNN5 (2026-10-23) — 입력란 폰트 통일.
//   textarea 는 브라우저 기본값이 monospace, input 은 sans-serif → 각오 한마디(long) vs 닉네임(short) 폰트 불일치.
//   font-family/font-weight 를 inherit 로 강제 → 두 입력 모두 상위(body/시스템) 폰트 사용.
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
  fontFamily: 'inherit', fontWeight: 'inherit', lineHeight: 1.5,
  ...darkInput,
};
// 팀원 칩 — 팀원(블루)·팀장(골드) 구분 유지
const memberPill: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
  ...darkTintBadge('#2563eb'), borderRadius: 12, fontSize: 11, fontWeight: 600,
};
const memberPillOwner: React.CSSProperties = {
  ...memberPill, ...darkTintBadge('#f59e0b'),
};
const pillDelBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: DARK_ACCENT_TEXT, fontSize: 14,
  cursor: 'pointer', padding: 0, marginLeft: 2,
};
const suggestBox: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
  background: DARK.surface, border: `1px solid ${DARK.border}`, borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)', zIndex: 10,
  maxHeight: 240, overflowY: 'auto',
};
const suggestItem: React.CSSProperties = {
  display: 'block', width: '100%', padding: '8px 10px', textAlign: 'left',
  background: DARK.surface, color: DARK.text, border: 'none', borderBottom: `1px solid ${DARK.border}`,
  cursor: 'pointer',
};

export default TournamentApplyPage;
